import type { Scenario } from './types'
import { transsulfurationSarah } from './transsulfuration-sarah'
import { methylationMaya } from './methylation-maya'
import { apoeLena } from './apoe-lena'
import { apoeDavid } from './apoe-david'
import { apoeSofia } from './apoe-sofia'
import { serotoninPriya } from './serotonin-priya'
import { statinRaj } from './statin-raj'
import { warfarinEleanor } from './warfarin-eleanor'
import { caffeineDerek } from './caffeine-derek'
import { thyroidCiara } from './thyroid-ciara'
// Batch 3 — 6 new scenarios (new series)
import { sleepTariq } from './sleep-tariq'
import { exercisePreethi } from './exercise-preethi'
import { autoimmuneChioma } from './autoimmune-chioma'
import { alcoholKenji } from './alcohol-kenji'
import { eczemaAisha } from './eczema-aisha'
// Batch 2 — 12 new scenarios
import { ironAmara } from './iron-amara'
import { vitamindCarlos } from './vitamind-carlos'
import { omega3Fatima } from './omega3-fatima'
import { histamineSaoirse } from './histamine-saoirse'
import { postpartumNadia } from './postpartum-nadia'
import { perimenopauseRuth } from './perimenopause-ruth'
import { bloodpressureMarcus } from './bloodpressure-marcus'
import { diabetesJames } from './diabetes-james'
import { goutHiro } from './gout-hiro'
import { antidepressantKofi } from './antidepressant-kofi'
import { opioidBrigid } from './opioid-brigid'
import { contraceptionYasmin } from './contraception-yasmin'
import { menopauseDiane } from './menopause-diane'
import { menopauseLiz } from './menopause-liz'
import { kidneyMalcolm } from './kidney-malcolm'
import { pcosNina } from './pcos-nina'
import { pcosFertilityAda } from './pcos-fertility-amara'
import { infertilityElena } from './infertility-elena'

// Add each new scenario data file here. Pages and the index page read from this registry.
export const SCENARIOS: Scenario[] = [
  transsulfurationSarah,
  thyroidCiara,
  // Batch 3 — new series
  sleepTariq,
  exercisePreethi,
  autoimmuneChioma,
  alcoholKenji,
  eczemaAisha,
  // Batch 1 — original 6
  methylationMaya,
  apoeLena,
  apoeDavid,
  apoeSofia,
  serotoninPriya,
  statinRaj,
  warfarinEleanor,
  caffeineDerek,
  // Batch 2 — 12 new scenarios
  ironAmara,
  vitamindCarlos,
  omega3Fatima,
  histamineSaoirse,
  postpartumNadia,
  perimenopauseRuth,
  bloodpressureMarcus,
  diabetesJames,
  goutHiro,
  antidepressantKofi,
  opioidBrigid,
  contraceptionYasmin,
  menopauseDiane,
  menopauseLiz,
  kidneyMalcolm,
  pcosNina,
  pcosFertilityAda,
  infertilityElena,
]

export const getScenarioBySlug = (slug: string): Scenario | undefined =>
  SCENARIOS.find((s) => s.slug === slug)

/** URL slug derived from series label, e.g. "Methylation & MTHFR" → "methylation-mthfr". */
export function scenarioCategorySlug(series: string): string {
  return series
    .toLowerCase()
    .replace(/\s*&\s*/g, '-')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
}

/** Case segment for /scenarios/:category/:case — strips category prefix when present. */
export function getScenarioCaseSlug(scenario: Scenario): string {
  const categoryPrefix = `${scenarioCategorySlug(scenario.series)}-`
  if (scenario.slug.startsWith(categoryPrefix)) {
    return scenario.slug.slice(categoryPrefix.length)
  }
  return scenario.slug
}

export function getScenarioPath(scenario: Scenario): string {
  return `/scenarios/${scenarioCategorySlug(scenario.series)}/${getScenarioCaseSlug(scenario)}`
}

export function getScenarioByCategoryAndCase(
  category: string,
  caseSlug: string,
): Scenario | undefined {
  return SCENARIOS.find(
    (s) =>
      scenarioCategorySlug(s.series) === category && getScenarioCaseSlug(s) === caseSlug,
  )
}

export function getScenariosByCategory(category: string): Scenario[] {
  return SCENARIOS.filter((s) => scenarioCategorySlug(s.series) === category)
}

export const getScenariosByCategorySlug = getScenariosByCategory

export function getCategorySeriesLabel(category: string): string | undefined {
  return getScenariosByCategory(category)[0]?.series
}

/** Common topic slugs that map to a scenario series category for category hubs. */
export const SCENARIO_CATEGORY_ALIASES: Record<string, string> = {
  perimenopause: 'menopause',
  hormones: 'hormones-mood',
  thyroid: 'hormones-mood',
  methylation: 'methylation-mthfr',
  mthfr: 'methylation-mthfr',
  apoe: 'apoe-brain-health',
  brain: 'apoe-brain-health',
  pharmacogenomics: 'pharmacogenomics',
  cardio: 'cardiovascular-risk',
  cardiovascular: 'cardiovascular-risk',
  cbs: 'cbs-transsulfuration',
  transsulfuration: 'cbs-transsulfuration',
  sulfur: 'cbs-transsulfuration',
}

export function resolveCategorySlug(slug: string): string | undefined {
  if (getScenariosByCategory(slug).length > 0) return slug
  const aliased = SCENARIO_CATEGORY_ALIASES[slug]
  if (aliased && getScenariosByCategory(aliased).length > 0) return aliased
  return undefined
}

export function isScenarioCategorySlug(slug: string): boolean {
  return resolveCategorySlug(slug) !== undefined && getScenarioBySlug(slug) === undefined
}

export function getAllCategoryHubPaths(): string[] {
  const paths = new Set<string>()
  for (const scenario of SCENARIOS) {
    paths.add(`/scenarios/${scenarioCategorySlug(scenario.series)}`)
  }
  for (const alias of Object.keys(SCENARIO_CATEGORY_ALIASES)) {
    if (resolveCategorySlug(alias)) paths.add(`/scenarios/${alias}`)
  }
  return Array.from(paths)
}

export type { Scenario } from './types'
